import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

const DEFAULT_THEME = {
  primary: '#1A2543',
  secondary: '#2E3A5C',
  iconPrimary: '#1A2543',
  iconSecondary: '#5B6B8C',
  illustrationPrimary: '#1A2543',
  onPrimary: '#FFFFFF',
  onSecondary: '#FFFFFF',
};

const buildMockBridgeScript = (appletId: string) => {
  const themeJson = JSON.stringify(DEFAULT_THEME);
  const appletIdJson = JSON.stringify(appletId || 'preview');
  return `
<script>
(function () {
  var theme = ${themeJson};
  var root = document.documentElement;
  if (root && root.style) {
    root.style.setProperty('--hermesz-primary', theme.primary);
    root.style.setProperty('--hermesz-secondary', theme.secondary);
    root.style.setProperty('--hermesz-icon-primary', theme.iconPrimary);
    root.style.setProperty('--hermesz-icon-secondary', theme.iconSecondary);
    root.style.setProperty('--hermesz-illustration', theme.illustrationPrimary);
    root.style.setProperty('--hermesz-on-primary', theme.onPrimary);
    root.style.setProperty('--hermesz-on-secondary', theme.onSecondary);
    root.style.setProperty('--brand', theme.primary);
    root.style.setProperty('--brand-secondary', theme.secondary);
    root.style.setProperty('--brand-press', theme.secondary);
    root.style.setProperty('--on-brand', theme.onPrimary);
  }

  window.Hermesz = {
    __ready: true,
    __preview: true,
    theme: theme,
    _appletId: ${appletIdJson},
    _platform: 'cms-preview',
    saveResult: function (payload) {
      try {
        console.log('[Hermész előnézet] saveResult', payload);
      } catch (e) {}
      return Promise.resolve({ ok: true, preview: true, queued: false });
    },
    getContext: function () {
      return Promise.resolve({
        appletId: ${appletIdJson},
        platform: 'cms-preview',
        preview: true,
      });
    },
    getTheme: function () {
      return Promise.resolve(theme);
    },
    close: function () {
      try {
        console.log('[Hermész előnézet] close');
      } catch (e) {}
      return Promise.resolve({ ok: true, preview: true });
    },
  };

  try {
    window.dispatchEvent(new CustomEvent('hermesz:ready', { detail: { preview: true } }));
    window.dispatchEvent(new CustomEvent('hermesz:theme', { detail: theme }));
  } catch (e) {}
})();
</script>`;
};

const injectBridge = (html: string, appletId: string) => {
  const bridge = buildMockBridgeScript(appletId);
  const headClose = /<\/head>/i;
  if (headClose.test(html)) {
    return html.replace(headClose, `${bridge}</head>`);
  }
  const htmlOpen = /<html[^>]*>/i;
  if (htmlOpen.test(html)) {
    return html.replace(htmlOpen, (m) => `${m}<head>${bridge}</head>`);
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${bridge}</head><body>${html}</body></html>`;
};

type PreviewSource =
  | { kind: 'html'; html: string }
  | { kind: 'url'; url: string }
  | { kind: 'empty'; message: string };

type Props = {
  open: boolean;
  onClose: () => void;
  appletId?: string | null;
  name?: string | null;
  htmlContent?: string | null;
  file?: File | null;
  contentUrl?: string | null;
};

/**
 * CMS preview of an HtmlApplet in a sandboxed iframe with a mock Hermesz bridge.
 */
const HtmlAppletPreviewDialog = ({
  open,
  onClose,
  appletId,
  name,
  htmlContent,
  file,
  contentUrl,
}: Props) => {
  const [fileHtml, setFileHtml] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);

  useEffect(() => {
    if (!open) {
      setFileHtml(null);
      setFileError(null);
      setLoadingFile(false);
      return;
    }

    if (!file) {
      setFileHtml(null);
      setFileError(null);
      return;
    }

    const lower = file.name.toLowerCase();
    if (lower.endsWith('.zip')) {
      setFileHtml(null);
      setFileError(
        'ZIP előnézet a szerkesztőben nem támogatott. Mentés után a feltöltött tartalom URL-jével, vagy .html / beillesztett HTML-lel előnézhető.'
      );
      return;
    }

    if (!lower.endsWith('.html') && !lower.endsWith('.htm')) {
      setFileHtml(null);
      setFileError('Csak .html fájl vagy beillesztett HTML előnézhető mentés előtt.');
      return;
    }

    setLoadingFile(true);
    setFileError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setFileHtml(typeof reader.result === 'string' ? reader.result : '');
      setLoadingFile(false);
    };
    reader.onerror = () => {
      setFileError('A fájl beolvasása sikertelen.');
      setLoadingFile(false);
    };
    reader.readAsText(file);
  }, [open, file]);

  const source: PreviewSource = useMemo(() => {
    const trimmed = (htmlContent ?? '').trim();
    if (trimmed) {
      return { kind: 'html', html: trimmed };
    }
    if (fileHtml && fileHtml.trim()) {
      return { kind: 'html', html: fileHtml };
    }
    if (fileError) {
      // Prefer saved content URL as fallback when zip selected / file unread.
      if (contentUrl) {
        return { kind: 'url', url: contentUrl };
      }
      return { kind: 'empty', message: fileError };
    }
    if (loadingFile) {
      return { kind: 'empty', message: 'Fájl betöltése…' };
    }
    if (contentUrl) {
      return { kind: 'url', url: contentUrl };
    }
    return {
      kind: 'empty',
      message:
        'Nincs megjeleníthető tartalom. Illesszen be HTML-t, válasszon .html fájlt, vagy mentés után nyissa meg újra az előnézetet.',
    };
  }, [htmlContent, fileHtml, fileError, loadingFile, contentUrl]);

  const srcDoc =
    source.kind === 'html'
      ? injectBridge(source.html, appletId ?? 'preview')
      : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      slotProps={{
        paper: { sx: { height: '90vh' } }
      }}
    >
      <DialogTitle>
        Előnézet{name ? `: ${name}` : ''}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary"
          }}>
          CMS előnézet — mock Hermész bridge (saveResult nem ment az API-ra)
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
        }}
      >
        {source.kind === 'empty' && (
          <Box sx={{
            p: 3
          }}>
            <Alert severity="info">{source.message}</Alert>
          </Box>
        )}
        {source.kind === 'url' && (
          <Box sx={{
            p: 1
          }}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              Mentett tartalom URL-je töltődik (relatív assetek működhetnek). A
              mock Hermész bridge csak beillesztett / .html előnézetnél injektálódik
              biztosan.
            </Alert>
          </Box>
        )}
        {(source.kind === 'html' || source.kind === 'url') && (
          <Box sx={{ flex: 1, minHeight: 480, bgcolor: '#f5f5f5' }}>
            <iframe
              title="HTML applet előnézet"
              src={source.kind === 'url' ? source.url : undefined}
              srcDoc={srcDoc}
              sandbox="allow-scripts allow-forms allow-modals allow-popups allow-downloads"
              style={{
                width: '100%',
                height: '100%',
                minHeight: 480,
                border: 0,
                background: '#fff',
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Bezárás
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HtmlAppletPreviewDialog;
